import { cn } from "@/lib/utils"
import { CircularProgress } from "@/components/ui/circular-progress"

export type ActionBadgeType =
  | "nutrition"
  | "exercise"
  | "screening"
  | "lifestyle"
  | "medication"

/** Brand color tints — for badges that aren't a clinical action `type`. */
export type ActionBadgeColor =
  | "light-blue"
  | "light-green"
  | "aubergine"
  | "light-pink"
  | "neutral"

export type ActionBadgeSize = "sm" | "md"

export interface ActionBadgeProps {
  label: string
  type?: ActionBadgeType
  /** Tint the badge a brand color (ignored when `type` is set). */
  color?: ActionBadgeColor
  /** "sm" is the default DS size; "md" enlarges the font and padding. */
  size?: ActionBadgeSize
  icon?: React.ReactNode
  progress?: number
  className?: string
}

const COLOR_STYLES: Record<ActionBadgeColor, string> = {
  "light-blue": "bg-light-blue-50 text-light-blue-900",
  "light-green": "bg-light-green-100 text-light-green-900",
  aubergine: "bg-aubergine-100 text-aubergine-900",
  "light-pink": "bg-light-pink-100 text-light-pink-900",
  neutral: "bg-neutral-50 text-neutral-500",
}

const SIZE_STYLES: Record<
  ActionBadgeSize,
  { container: string; iconSlot: string; label: string }
> = {
  sm: {
    container: "gap-1 py-1 pr-2 pl-1.5",
    iconSlot: "size-4",
    label: "type-body-sm",
  },
  md: {
    container: "gap-1.5 py-1.5 pr-3 pl-2.5",
    iconSlot: "size-5",
    label: "type-body-md",
  },
}

const TYPE_STYLES: Record<
  ActionBadgeType,
  { bg: string; text: string; progressColor: string }
> = {
  nutrition: {
    bg: "bg-green-50",
    text: "text-green-700",
    progressColor: "text-green-600",
  },
  exercise: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    progressColor: "text-blue-600",
  },
  screening: {
    bg: "bg-aubergine-50",
    text: "text-aubergine-700",
    progressColor: "text-aubergine-600",
  },
  lifestyle: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    progressColor: "text-amber-700",
  },
  medication: {
    bg: "bg-error-100",
    text: "text-error-700",
    progressColor: "text-error-600",
  },
}

export function ActionBadge({
  label,
  type,
  color,
  size = "sm",
  icon,
  progress,
  className,
}: ActionBadgeProps) {
  const hasProgress = progress !== undefined
  const styles = type ? TYPE_STYLES[type] : null
  const sizing = SIZE_STYLES[size]

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full",
        sizing.container,
        type
          ? [styles!.bg, styles!.text]
          : color
            ? COLOR_STYLES[color]
            : "border border-neutral-200 text-neutral-900",
        className
      )}
    >
      {icon && (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center",
            sizing.iconSlot
          )}
        >
          {icon}
        </span>
      )}
      <span
        className={cn(
          "flex items-center justify-center pl-[2px] font-medium",
          sizing.label
        )}
        style={{ fontFeatureSettings: "'ss02' 1" }}
      >
        {label}
      </span>
      {hasProgress && styles && (
        <CircularProgress
          value={progress}
          size={16}
          colorClassName={styles.progressColor}
          trackClassName={`${styles.progressColor} opacity-20`}
          strokeWidth={2}
        />
      )}
    </span>
  )
}
